import {useState, useEffect} from "react"
import moment from "moment";
import { Tabs } from 'antd';

import PageHeading from "../../shared/containers/PageHeading";
import LineChart from "../../shared/components/LineChart";
import DonatChart from "../../shared/components/DonatChart";
import DataCard from "../../shared/components/DataCard";

import PageStatistics from "../../modules/PageStatistics";

import styles from "./insight-page.module.scss";

import stats from "./statItems";
import {lineData, donatData, avgGrossClientData, avgNetClientData, avgGrossDelegateData, avgNetDelegateData} from "./data";

import {getAll} from "../../shared/services/insights"
import DashboardSearch from "../../modules/Dashboard/DashboardSearch";

const { TabPane } = Tabs;

const hasEventDate = ({event_date}) => Boolean(event_date)

const InsightsPage = () => {
    const [dateRange, setDateRange] = useState(null);
    const changeDateRange = (value) => {
        setDateRange([value[0], value[1]])
    }
    const [data, setData] = useState(null);

    useEffect(()=> {
        const fetchData = async () => {
            try {
                const result = await getAll();
                setData(result)
                // console.log(result)
            }
            catch(error) {
                console.log(error)
            }
        }

        fetchData()
    }, [])

    const onChange = (key) => {
        console.log(key);
    };

    if(!data) {
        return (
            <PageHeading title="Insights" subtitle="Statistics page">

            </PageHeading>
        )
    }

    const {missions: fetchMissons, users: fetchUsers} = data;
    const users = dateRange ? fetchUsers.filter(({date}) => {
        const userDate = moment(date);
        if(dateRange) {
            return (dateRange[0] <= userDate && dateRange[1] >= userDate)
        }
    }) : fetchUsers;

    const missions = dateRange ? fetchMissons.filter(({date}) => {
        const missionDate = moment(date);
        if(dateRange) {
            return (dateRange[0] <= missionDate && dateRange[1] >= missionDate)
        }
    }) : fetchMissons;

    const missionAmount = ({payment_amount, expected_hours}) => payment_amount !== null && payment_amount !== undefined && payment_amount !== ""
        ? Number(payment_amount) : Number(expected_hours || 0) * 50 + 30;
    const avgMissionsCost = (missions.reduce((acum, mission) => acum + missionAmount(mission), 0) / missions.length).toFixed(0)
    const avgMissionsSpend = (missions.reduce((acum, {expected_hours}) => acum + expected_hours * 20 + 20, 0) / missions.length).toFixed(0)
    const avgMissionsRevenue = avgMissionsCost - avgMissionsSpend
    const totalMissionsTime = missions.reduce((acum, {expected_hours}) => acum + expected_hours, 0);
    const avgMissionTime = (totalMissionsTime / missions.length).toFixed(0)

    const missionsValues = {
        avgMissionsCost: avgMissionsCost !== "NaN" ? avgMissionsCost : "",
        avgMissionsRevenue: isNaN(avgMissionsRevenue) ? "" : avgMissionsRevenue,
        avgTime: avgMissionTime !== "NaN" ? `${avgMissionTime}:00 / ${totalMissionsTime}:00` : ""
    }

    const uniqueMonth = missions.filter(hasEventDate).reduce((acum, {event_date}) => {
        const data = event_date.slice(0, 7);
        if(!acum.includes(data)) {
            return [...acum, data]
        }
        return acum;
    }, []).map(item => ({Date: item, scales: 0})).sort((a, b)=> {
        const [year1, month1] = a.Date.split("-")
        const [year2, month2] = b.Date.split("-")
        if(year1 > year2){
            return 1;
        }
        if(year1 === year2) {
            if(month1 > month2) {
                return 1;
            }
            if(month2 > month1) {
                return -1;
            }
            return 0;
        }

        return -1;
    });

    const revenuePerMonth = missions.filter(hasEventDate).reduce((acum, mission)=> {
        const {event_date, expected_hours} = mission;
        const index = acum.findIndex(({Date}) => Date === event_date.slice(0, 7));
        acum[index].scales += missionAmount(mission) - (Number(expected_hours || 0) * 20 + 20);
        return acum;

    }, [...uniqueMonth])

    const donatMissionsData = [
        {
            type: 'Factory visit',
            value: 0,
        },
        {
            type: 'Site visit',
            value: 0,
        },
        {
            type: 'Store check',
            value: 0,
        },
        {
            type: 'Other',
            value: 0,
        },
    ];

    const revenuePerMission = missions.reduce((acum, {category, expected_hours})=> {
        const value = (expected_hours * 50 + 50) - (expected_hours * 20 + 20);
        if(category === 1) {
            acum[0].value += value;
        }
        if(category === 2) {
            acum[1].value += value;
        }
        if(category === 3) {
            acum[2].value += value;
        }
        if(category === 4) {
            acum[3].value += value;
        }
        return acum;
    }, donatMissionsData);

    const clients = users.filter(({type}) => type === 2 || type === 3);
    const delegates = users.filter(({type}) => type === 1);

    const allMissionsCost = missions.reduce((acum, mission) => acum + missionAmount(mission), 0);

    const avgGrossRevenueClient = (allMissionsCost / clients.length).toFixed(0);
    const allMissionsSpend = missions.reduce((acum, {expected_hours}) => acum + expected_hours * 20 + 20, 0);
    const avgNetRevenueClient = ((allMissionsCost - allMissionsSpend) / clients.length).toFixed(0);
    const avgGrossRevenueDelegate = (allMissionsSpend / delegates.length).toFixed(0);

    const clientsValues = {
        avgClientRevenue: avgNetRevenueClient !== "NaN" ? avgNetRevenueClient : "",
        avgMissionsRevenue: isNaN(avgMissionsRevenue) ? "" : avgMissionsRevenue,
        avgTime: avgMissionTime !== "NaN" ? `${avgMissionTime}:00 / ${totalMissionsTime}:00` : "",
    }

    const donatClientsData = [
        {
            type: 'Client (Individual)',
            value: clients.filter(({type}) => type === 2).length,
        },
        {
            type: 'Client (Company)',
            value: clients.filter(({type}) => type === 3).length,
        },
    ];

    const uniqueClientsMonth = clients.reduce((acum, {date}) => {
        const data = date.slice(0, 7);
        if(!acum.includes(data)) {
            return [...acum, data]
        }
        return acum;
    }, []).map(item => ({Date: item, scales: 0}))

    const clientsPerMonth = clients.reduce((acum, {date}) => {
        const index = acum.findIndex(({Date}) => Date === date.slice(0, 7));
        acum[index].scales += 1;
        return acum;
    }, [...uniqueClientsMonth])

    const delegatesValues = {
        avgDelegateRevenue: (allMissionsSpend / delegates.length).toFixed(0) !== "NaN" ? (allMissionsSpend / delegates.length).toFixed(0) :  "",
        avgMissionsRevenue: isNaN(avgMissionsRevenue) ? "" : avgMissionsRevenue,
        avgTime: avgMissionTime !== "NaN" ? `${avgMissionTime}:00 / ${totalMissionsTime}:00` : "",
    }

    const uniqueDelegatesMonth = delegates.reduce((acum, {date}) => {
        const data = date.slice(0, 7);
        if(!acum.includes(data)) {
            return [...acum, data]
        }
        return acum;
    }, []).map(item => ({Date: item, scales: 0}))

    const DelegatesPerMonth = delegates.reduce((acum, {date}) => {
        const index = acum.findIndex(({Date}) => Date === date.slice(0, 7));
        acum[index].scales += 1;
        return acum;
    }, [...uniqueDelegatesMonth])

    const factoryVisit = missions.filter(({category}) => category === 1).length;
    const siteVisit = missions.filter(({category}) => category === 2).length;
    const storeCheck = missions.filter(({category}) => category === 3).length;
    const other = missions.filter(({category}) => category === 4).length;

    const donatDelegatesData = [
        {
            type: 'Factory visit',
            value: +((factoryVisit / missions.length) * 100).toFixed(2),
        },
        {
            type: 'Site visit',
            value: +((siteVisit / missions.length) * 100).toFixed(2),
        },
        {
            type: 'Store check',
            value: +((storeCheck / missions.length) * 100).toFixed(2),
        },
        {
            type: 'Other',
            value: +((other / missions.length) * 100).toFixed(2),
        },
    ];


    return (
        <>
            <PageHeading title="Insights" subtitle="Statistics page">
                <DashboardSearch onChange={changeDateRange}/>
            </PageHeading>
            <Tabs defaultActiveKey="1" onChange={onChange}>
                <TabPane tab="Mission Analytics" key="1">
                    <PageStatistics values={missionsValues} items={stats.missions}/>
                    <div className={styles.chartsWrapper}>
                        <div>
                            <LineChart title="Revenue per month" data={revenuePerMonth} />
                        </div>
                        <div>
                            <DonatChart title="Revenue by Mission Type" data={revenuePerMission} />
                        </div>
                    </div>
                    <div className={styles.dataWrapper}>
                        <DataCard {...avgGrossClientData} total={avgGrossRevenueClient} />
                        <DataCard {...avgNetClientData} total={avgNetRevenueClient} />
                        <DataCard {...avgGrossDelegateData} total={avgGrossRevenueDelegate} />
                        <DataCard {...avgNetDelegateData} total={avgGrossRevenueDelegate} />
                    </div>
                </TabPane>
                <TabPane tab="Client Statistics Analysis" key="2">
                    <PageStatistics items={stats.clients} values={clientsValues}/>
                    <div className={styles.chartsWrapper}>
                        <div>
                            <LineChart title="Clients this month" data={clientsPerMonth} />
                        </div>
                        <div>
                            <DonatChart title="Client Types" data={donatClientsData} />
                        </div>
                    </div>
                </TabPane>
                <TabPane tab="Delegate Statistics Analysis" key="3">
                    <PageStatistics items={stats.delegates} values={delegatesValues} />
                    <div className={styles.chartsWrapper}>
                        <div>
                            <LineChart title="Delegates this month" data={DelegatesPerMonth} />
                        </div>
                        <div>
                            <DonatChart title="Missions by categories" data={donatDelegatesData} />
                        </div>
                    </div>
                </TabPane>
            </Tabs>
        </>
    )
}

export default InsightsPage;
